import { useState } from 'react';
import { CaretDownIcon, TerminalWindowIcon } from '@phosphor-icons/react';

import classNames from '@/utils/classnames';

import { splitJobReport } from '../../stores/background-jobs';

/** The headline with its backticked spans — the command — set as code, the way the model reads them. */
function Headline({ text }: { text: string }) {
  return text.split('`').map((part, i) =>
    i % 2 === 1 ? (
      <code key={i} className="font-mono text-2xs text-fg-tertiary">
        {part}
      </code>
    ) : (
      part
    ),
  );
}

/**
 * A background job's report, at the point in the thread where the agent loop handed it to the
 * model.
 *
 * Neither side of the conversation wrote it, so it is a notice rather than a bubble: the sentence
 * saying which job ended and how, with the output folded beneath it. Collapsed by default because
 * the output is for the model — a reader wants to know that the build finished, and opens it only
 * to see why it failed.
 */
export function JobReportNotice({ content }: { content: string }) {
  const [open, setOpen] = useState(false);
  const { headline, output } = splitJobReport(content);

  return (
    <div className="pl-8.5">
      <div className="flex flex-col gap-1.5 rounded-lg border border-line-subtle bg-surface-1 px-3 py-2">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
          disabled={!output}
          className="flex items-center gap-2 text-left"
        >
          <TerminalWindowIcon size={14} className="shrink-0 text-fg-tertiary" />
          <span className="min-w-0 flex-1 text-xs text-fg-secondary">
            <Headline text={headline} />
          </span>
          {output && (
            <CaretDownIcon
              size={12}
              className={classNames('shrink-0 text-fg-tertiary transition-transform', open && 'rotate-180')}
            />
          )}
        </button>
        {open && (
          <pre className="max-h-60 overflow-auto whitespace-pre-wrap rounded-md bg-surface-2 px-2 py-1.5 font-mono text-2xs text-fg-secondary">
            {output}
          </pre>
        )}
      </div>
    </div>
  );
}
